import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_agglos-producer/artifacts/zs2rc3fd_10.png";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const formatError = (detail) => {
    if (!detail) return "Une erreur est survenue";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Connexion réussie");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4 py-10 bg-gradient-to-br from-[#0f1f36] via-[#1E3A5F] to-[#0f1f36]">
      <Card className="w-full max-w-md shadow-2xl border-0" data-testid="admin-login-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-40 mb-4">
            <img src={LOGO} alt="AGRABAT" className="w-full" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1E3A5F] flex items-center justify-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#F5A623]" />
            Espace Administrateur
          </CardTitle>
          <p className="text-sm text-gray-500 mt-2">Consultez les devis et messages reçus</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Adresse email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@agrabat.sn"
                data-testid="admin-login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="admin-login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1E3A5F] hover:bg-[#2a4d7a] text-white font-semibold py-6"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connexion...</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" /> Se connecter</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
